import { useCallback, useEffect, useMemo, useState } from "react";
import { db } from "~/db/db";
import { useGetBateuLevou } from "~hooks/useSincronizacao";
import { bateuLevouMock } from "../mocks/new/BateuLevou.mock";
import useVendedor from "~fetchs/useVendedor";
import { meses, formatMes } from "../utils/formatters";
import { extractUpdatedAt } from "../utils/extractUpdatedAt";
import { usarMock } from "../utils/dataSource";


const fmtNumero = new Intl.NumberFormat("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtMoeda  = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });

const formatarNumero     = (value) => (value == null ? "0,00" : fmtNumero.format(value));
const formatarMoeda      = (value) => (value == null ? "R$0,00" : fmtMoeda.format(value).replace("R$\u00a0", "R$"));
const formatarPorcentagem = (value) => (value == null ? "0%" : `${Math.round(value * 100)}%`);


const quebrarPorPalavra = (texto, maxLen) => {
  const linhas = [];
  let atual = "";
  for (const palavra of texto.split(/\s+/).filter(Boolean)) {
    if (!atual) atual = palavra;
    else if (`${atual} ${palavra}`.length <= maxLen) atual += ` ${palavra}`;
    else { linhas.push(atual); atual = palavra; }
  }
  if (atual) linhas.push(atual);
  return linhas;
};


const truncarLabel = (codigo, razaoSocial, maxLen = 30, maxLinhas = 2) => {
  const texto = `${codigo} ${(razaoSocial ?? "").trim()}`.trim();
  let linhas = quebrarPorPalavra(texto, maxLen);
  if (linhas.length > maxLinhas) {
    linhas = linhas.slice(0, maxLinhas);
    linhas[maxLinhas - 1] = `${linhas[maxLinhas - 1]}...`;
  }
  return linhas;
};


const labelMesAno = (mesAno) => {
  if (!mesAno) return "";
  const [ano, mes] = mesAno.split("-");
  return `${meses[Number(mes) - 1]} de ${ano}`;
};


const mesAnoAnterior = (mesAno) => {
  if (!mesAno) return null;
  const [ano, mes] = mesAno.split("-").map(Number);
  const d = new Date(ano, mes - 2, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};


const mesMaisRecente = (items) => {
  const chaves = (items ?? [])
    .map((i) => (i?.inicioMes ? String(i.inicioMes).slice(0, 7) : null))
    .filter((m) => m && m > "0001-01");
  return chaves.length ? chaves.reduce((max, m) => (m > max ? m : max)) : null;
};



const sumBy     = (arr, key) => arr.reduce((s, i) => s + (i?.[key] ?? 0), 0);


const fields = [
  "clientesCampanha",
  "clientesAtingindoMeta",
  "progressoVolume",
  "limiteProgressoVolume",
  "progressoDesconto",
  "limiteProgressoDesconto",
];


const normalizarCards = (cards) =>
  Array.isArray(cards) ? cards : cards ? [cards] : [];

const agregarCards = (cards, produtoFiltro = null) => {
  const arr = normalizarCards(cards);
  const alvo = produtoFiltro ? arr.filter((c) => c.produto === produtoFiltro) : arr;
  return Object.fromEntries(fields.map((f) => [f, sumBy(alvo, f)]));
};
const avgNonNull = (arr, key) => {
  const valid = arr.filter(i => i?.[key] != null);
  return valid.length ? valid.reduce((s, i) => s + i[key], 0) / valid.length : 0;
};
const groupByKey = (arr, getKey) => {
  const map = new Map();
  for (const item of arr) {
    const k = getKey(item);
    if (!map.has(k)) map.set(k, []);
    map.get(k).push(item);
  }
  return map;
};


const agregarPorMes = (items) => {
  const map = new Map();
  for (const item of items) {
    const key = item.inicioMes;
    if (!map.has(key)) map.set(key, { inicioMes: key, realizado: 0, gap: 0 });
    const entry = map.get(key);
    entry.realizado += item.realizado ?? 0;
    entry.gap       += item.gap       ?? 0;
  }
  return [...map.values()].sort((a, b) => new Date(b.inicioMes) - new Date(a.inicioMes));
};



const flattenVendedorBateuLevou = (rawArr, vendedorSelecionado) => {
  if (!Array.isArray(rawArr) || !rawArr.length) return rawArr ?? [];
  if (!rawArr.some(v => Array.isArray(v?.basesBateuLevou))) return rawArr;

  const filtered = vendedorSelecionado !== null
    ? rawArr.filter(v => v?.id === vendedorSelecionado)
    : rawArr;
  const flatItems = filtered.flatMap(v => v?.basesBateuLevou ?? []);

  if (filtered.length <= 1) return flatItems;


  const clientMap = groupByKey(
    flatItems.filter(i => i.id > 0),
    i => `${i.codigo}|${i.inicioMes ?? ""}`
  );
  const merged = Array.from(clientMap.values()).map(g => ({
    ...g[0],
    volume:       sumBy(g, 'volume'),
    meta:         sumBy(g, 'meta'),
    atingimento:  avgNonNull(g, 'atingimento'),
    descontoRs:   sumBy(g, 'descontoRs'),
    descontoRsSc: sumBy(g, 'descontoRsSc'),
    verMais:      g.flatMap(x => x.verMais ?? []),
  }));

  const specials   = groupByKey(flatItems.filter(i => i.id < 0), i => i.id);
  const getSpecial = (id) => specials.get(id) ?? [];


  const mergeCards = (grp) => {
    const porProduto = groupByKey(
      grp.flatMap(i => normalizarCards(i.cards)),
      c => c.produto
    );
    return Array.from(porProduto.entries()).map(([produto, cards]) => ({
      produto,
      ...agregarCards(cards),
    }));
  };

  return [
    ...merged,
    { id: -1,  clienteId: 0, cards: mergeCards(getSpecial(-1)) },
    { id: -2,  clienteId: 0, cards: mergeCards(getSpecial(-2)) },
    { id: -3,  metasNegociadas:    getSpecial(-3).flatMap(i => i.metasNegociadas    ?? []) },
    { id: -4,  metasNegociadas:    getSpecial(-4).flatMap(i => i.metasNegociadas    ?? []) },
    { id: -5,  proximosMeta:       getSpecial(-5).flatMap(i => i.proximosMeta       ?? []) },
    { id: -6,  proximosMeta:       getSpecial(-6).flatMap(i => i.proximosMeta       ?? []) },
    { id: -7,  linhaTempoDesconto: getSpecial(-7).flatMap(i => i.linhaTempoDesconto ?? []) },
    { id: -8,  linhaTempoVolume:   getSpecial(-8).flatMap(i => i.linhaTempoVolume   ?? []) },
    { id: -9,  filtros: {
      gruposCentralizadores: [...new Set(getSpecial(-9).flatMap(i => i.filtros?.gruposCentralizadores ?? []))],
      gruposProduto:         [...new Set(getSpecial(-9).flatMap(i => i.filtros?.gruposProduto         ?? []))],
    }},
    { id: -10, createdAt: getSpecial(-10).map(i => i.createdAt).find(Boolean) ?? null },
  ];
};

const idsContratoAntigo = { "-8": -10, "-7": -9, "-6": -8, "-5": -7, "-4": -5 };

const normalizarContratoBases = (bases) => {
  if (!Array.isArray(bases)) return bases;

  const ehContratoAntigo = bases.some(
    (b) => b?.id === -5 && Array.isArray(b?.linhaTempoDesconto)
  );
  if (!ehContratoAntigo) return bases;

  const remapped = bases.map((item) =>
    item && item.id < 0 && idsContratoAntigo[item.id] != null
      ? { ...item, id: idsContratoAntigo[item.id] }
      : item
  );

  const metasAtual    = remapped.find((b) => b?.id === -3);
  const proximosAtual = remapped.find((b) => b?.id === -5);

  if (metasAtual && !remapped.some((b) => b?.id === -4)) {
    remapped.push({ id: -4, metasNegociadas: metasAtual.metasNegociadas ?? [] });
  }
  if (proximosAtual && !remapped.some((b) => b?.id === -6)) {
    remapped.push({ id: -6, proximosMeta: proximosAtual.proximosMeta ?? [] });
  }

  return remapped;
};

const normalizarVendedores = (vendedores) =>
  (Array.isArray(vendedores) ? vendedores : []).map((v) =>
    v?.basesBateuLevou
      ? { ...v, basesBateuLevou: normalizarContratoBases(v.basesBateuLevou) }
      : v
  );

const extrairDados = (source) => {
  if (Array.isArray(source?.[0]?.[0]?.data)) {
    const payload = source[0][0];
    if (!payload?.isSuccess) return [];
    return normalizarVendedores(payload?.data?.[0] ?? []);
  }
  const arr = source?.data?.data?.[0] ?? source?.data?.[0] ?? source?.[0] ?? null;
  return normalizarVendedores(Array.isArray(arr) ? arr : []);
};

const montarLinhas = (allItems) =>
  allItems
    .filter((c) => c.id > 0)
    .map((c) => ({
      id: c.id,
      type: c.verMais?.length > 0 ? "group" : "flat",
      mesAno: c.inicioMes ? String(c.inicioMes).slice(0, 7) : null,
      grupoCentralizador: c.grupoCentralizador ?? "Sem Grupo",
      cliente: `${c.codigo} - ${c.razaoSocial}`,
      aceite: c.aceite,
      volume: formatarNumero(c.volume),
      meta: formatarNumero(c.meta),
      atingimento: formatarPorcentagem(c.atingimento),
      atingimentoValor: c.atingimento ?? 0,
      desconto: formatarMoeda(c.descontoRs),
      details: (c.verMais ?? []).map((d, i) => ({
        id: `${c.id}-detail-${i}`,
        cliente: `${d.codigo} - ${d.razaoSocial}`,
        produto: d.produto,
        volume: formatarNumero(d.volume),
        meta: formatarNumero(d.meta),
        atingimento: formatarPorcentagem(d.atingimento),
        desconto: formatarMoeda(d.descontoRs),

        _volume: d.volume ?? 0,
        _meta: d.meta ?? 0,
        _desconto: d.descontoRs ?? 0,
      })),
    }));


export const montarTopMetas = (items = []) => {
  const top = [...items].sort((a, b) => (b.metaOficial ?? 0) - (a.metaOficial ?? 0)).slice(0, 5);
  return {
    labels: top.map((m) => truncarLabel(m.codigo, m.razaoSocial)),
    data:   top.map((m) => m.metaOficial),
  };
};

export const montarTopProximos = (items = []) => {
  const top = [...items].sort((a, b) => (a.gap ?? 0) - (b.gap ?? 0)).slice(0, 5);
  return {
    labels:       top.map((m) => truncarLabel(m.codigo, m.razaoSocial)),
    atingidoData: top.map((m) => m.atingido),
    gapData:      top.map((m) => m.gap),
  };
};

export const filtrarRowsPorProduto = (rows, produto) => {
  if (!produto) return rows;
  return rows.reduce((acc, row) => {
    const dets = (row.details ?? []).filter((d) => d.produto === produto);
    if (!dets.length) return acc;
    const volume   = sumBy(dets, "_volume");
    const meta     = sumBy(dets, "_meta");
    const desconto = sumBy(dets, "_desconto");
    const atingimentoValor = meta > 0 ? volume / meta : 0;
    acc.push({
      ...row,
      volume:      formatarNumero(volume),
      meta:        formatarNumero(meta),
      atingimento: formatarPorcentagem(atingimentoValor),
      atingimentoValor,
      desconto:    formatarMoeda(desconto),
      details:     dets,
    });
    return acc;
  }, []);
};

const montarCards = (cards, period) => {
  const pct = (v, lim) => (lim > 0 ? Math.round((v / lim) * 100) : 0);
  const semDados = fields.every((f) => !cards[f]);
  return {
    period,
    semDados,
    statCards: [
      { label: " Clientes/Produtos em Campanha:",    value: `${cards.clientesCampanha ?? 0} clientes`,      color: "blue"  },
      { label: "Clientes/Produtos Atingindo Meta:", value: `${cards.clientesAtingindoMeta ?? 0} clientes`, color: "green" },
    ],
    progressItems: [
      {
        label:        "Volume (t)",
        currentValue: `${formatarNumero(cards.progressoVolume)}t`,
        percentage:   pct(cards.progressoVolume, cards.limiteProgressoVolume),
        targetValue:  `${formatarNumero(cards.limiteProgressoVolume)}t`,
        color:        "blue",
      },
      {
        label:        "Desconto (R$)",
        currentValue: formatarMoeda(cards.progressoDesconto),
        percentage:   pct(cards.progressoDesconto, cards.limiteProgressoDesconto),
        targetValue:  formatarMoeda(cards.limiteProgressoDesconto),
        color:        "green",
      },
    ],
  };
};

export const montarLinhaTempo = (descontoItems, volumeItems) => {
  const desc   = [...agregarPorMes(descontoItems)].reverse();
  const vol    = [...agregarPorMes(volumeItems)].reverse();
  const labels = desc.map((d) => formatMes(d.inicioMes));
  return {
    descontoLabels:    labels,
    descontoRealizado: desc.map((d) => d.realizado),
    descontoGap:       desc.map((d) => d.gap),
    volumeLabels:      labels,
    volumeRealizado:   vol.map((d) => d.realizado),
    volumeGap:         vol.map((d) => d.gap),
    inicioMesIndex:    labels.length - 1,
  };
};

const buildFiltros = (filtros) => ({
  grupoCentralizadorOptions: [
    { value: "", label: "Sem Grupo" },
    ...(filtros?.gruposCentralizadores ?? [])
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b, "pt-BR"))
      .map((g) => ({ value: g, label: g })),
  ],
  grupoProdutoOptions: [
    { value: "", label: "Todos os Grupos" },
    ...(filtros?.gruposProduto ?? []).map((g) => ({ value: g, label: g })),
  ],
});


const salvarNoDexie = async (items) => {
  try {
    await db.bateuLevou.clear();
    await db.bateuLevou.add({ payload: items, syncedAt: new Date().toISOString() });
  } catch (err) {
    console.error('Erro ao salvar bateuLevou no Dexie:', err);
  }
};

export const useBateuLevouData = () => {
  const { vendedorSelecionado } = useVendedor();
  const [rawData, setRawData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const vendedorPronto = vendedorSelecionado !== undefined;

  const hasMock = usarMock(bateuLevouMock);

  const { data: bateuData, isFetching, isRefetching } =
    useGetBateuLevou(!hasMock && navigator.onLine, 0, 0, null);

  const loadFromDexie = useCallback(async () => {
    try {
      const rows = await db.bateuLevou.toArray();
      if (rows?.length) setRawData(rows[0]?.payload ?? null);
    } catch (err) {
      console.error('Erro ao carregar bateuLevou do Dexie:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);


  useEffect(() => {
    if (hasMock) {
      setRawData(extrairDados(bateuLevouMock));
      setIsLoading(false);
      return;
    }
    loadFromDexie();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  useEffect(() => {
    if (hasMock) return;
    window.addEventListener('online', loadFromDexie);
    return () => window.removeEventListener('online', loadFromDexie);
  }, [hasMock, loadFromDexie]);


  useEffect(() => {
    if (hasMock || !navigator.onLine) return;

    if (isFetching || isRefetching) {
      setIsRefreshing(true);
      return;
    }

    if (!bateuData) return;

    const items = extrairDados(bateuData);
    setRawData(items);
    salvarNoDexie(items);
    setIsRefreshing(false);
    setIsLoading(false);
  }, [hasMock, isFetching, isRefetching, bateuData]);

  const allItems = useMemo(
    () => flattenVendedorBateuLevou(rawData ?? [], vendedorSelecionado),
    [rawData, vendedorSelecionado]
  );


  const mapaId = useMemo(
    () => new Map(allItems.map(item => [item.id, item])),
    [allItems]
  );

  const rows = useMemo(() => montarLinhas(allItems), [allItems]);



  const mesAtual = useMemo(() => {

    const dasLinhas = mesMaisRecente(allItems.filter((i) => i.id > 0));
    if (dasLinhas) return dasLinhas;

    const timeline = [
      ...(mapaId.get(-7)?.linhaTempoDesconto ?? []),
      ...(mapaId.get(-8)?.linhaTempoVolume ?? []),
    ];
    const doTimeline = mesMaisRecente(timeline);
    if (doTimeline) return doTimeline;

    const createdAt = mapaId.get(-10)?.createdAt;
    if (createdAt) {
      const m = String(createdAt).slice(0, 7);
      if (m > "0001-01") return m;
    }
    return null;
  }, [mapaId, allItems]);

  const mesAnterior = useMemo(() => mesAnoAnterior(mesAtual), [mesAtual]);

  const periodoAtual = useMemo(() => {
    const cards = agregarCards(mapaId.get(-1)?.cards);
    return montarCards(cards, labelMesAno(mesAtual));
  }, [mapaId, mesAtual]);

  const periodoAnterior = useMemo(() => {
    const cards = agregarCards(mapaId.get(-2)?.cards);
    return montarCards(cards, labelMesAno(mesAnterior));
  }, [mapaId, mesAnterior]);

  const topMetasNegociadas = useMemo(
    () => montarTopMetas(mapaId.get(-3)?.metasNegociadas ?? []),
    [mapaId]
  );

  const topMetasNegociadasAnterior = useMemo(
    () => montarTopMetas(mapaId.get(-4)?.metasNegociadas ?? []),
    [mapaId]
  );

  const topProximosMeta = useMemo(
    () => montarTopProximos(mapaId.get(-5)?.proximosMeta ?? []),
    [mapaId]
  );

  const topProximosMetaAnterior = useMemo(
    () => montarTopProximos(mapaId.get(-6)?.proximosMeta ?? []),
    [mapaId]
  );

  const linhaTempoDescontoRaw = useMemo(() => mapaId.get(-7)?.linhaTempoDesconto ?? [], [mapaId]);
  const linhaTempoVolumeRaw   = useMemo(() => mapaId.get(-8)?.linhaTempoVolume   ?? [], [mapaId]);

  const linhaTempo = useMemo(
    () => montarLinhaTempo(linhaTempoDescontoRaw, linhaTempoVolumeRaw),
    [linhaTempoDescontoRaw, linhaTempoVolumeRaw]
  );

  const filtros = useMemo(() => buildFiltros(mapaId.get(-9)?.filtros), [mapaId]);

  const updatedAt = useMemo(() => extractUpdatedAt(rawData), [rawData]);

  return {
    rows,
    mesAtual,
    mesAnterior,
    periodoAtual,
    periodoAnterior,
    topMetasNegociadas,
    topMetasNegociadasAnterior,
    topProximosMeta,
    topProximosMetaAnterior,
    linhaTempo,
    linhaTempoDescontoRaw,
    linhaTempoVolumeRaw,
    filtros,
    updatedAt,
    isLoading: isLoading || !vendedorPronto,
    isRefreshing,
  };
};

